/* eslint-disable @typescript-eslint/no-invalid-void-type */
import { datadogRum } from '@datadog/browser-rum';
import { useMutation, UseMutationOptions } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import {
  patchPlanStatus,
  PatchPlanStatusError,
  PatchStatusRequest,
  Plan,
} from '@cfa/pathway-fe-shared/xp';

import { itemByLanguage } from '@/utils/language';
import { invalidatePlanArchiveStatusQueries } from '@/utils/queryInvalidation';

import ToastMessageBlock from '@/components/Toasts/SuccessToast';

type MutateVariables<TBody extends PatchStatusRequest | undefined> =
  TBody extends undefined ? PatchStatusRequest : void;

type UpdatePlanStatusOptions<TBody extends PatchStatusRequest | undefined> =
  Omit<
    UseMutationOptions<void, PatchPlanStatusError, MutateVariables<TBody>>,
    'mutationKey' | 'mutationFn' | 'throwOnError'
  > & {
    body?: TBody;
  };

/**
 * Mutation hook to update a plan's status.
 *
 * @param plan - The plan whose status is being updated.
 * @param options - Optional `useMutation` overrides plus a default `body`.
 * @example
 * const { mutate } = useUpdatePlanStatus(plan, {
 *   body: {
 *     status: 'ACTIVE',
 *   },
 * });
 * mutate();
 *
 * const { mutate } = useUpdatePlanStatus(plan);
 * mutate({ status: 'ACTIVE' });
 */
export const useUpdatePlanStatus = <
  TBody extends PatchStatusRequest | undefined = undefined,
>(
  plan?: Plan,
  options?: UpdatePlanStatusOptions<TBody>,
) => {
  const { t } = useTranslation();
  return useMutation<void, PatchPlanStatusError, MutateVariables<TBody>>({
    ...options,
    mutationKey: ['patchPlanStatus', plan?.id],
    mutationFn: async vars => {
      const body = options?.body ?? vars;
      if (!body) {
        throw new Error('[useUpdatePlanStatus] Body is required');
      }
      if (!plan) {
        throw new Error('[useUpdatePlanStatus] Plan is required');
      }
      await patchPlanStatus({
        path: { planId: plan.id },
        body,
        throwOnError: true,
      });
    },
    onSuccess: async (data, variables, onMutateResult, context) => {
      toast.custom(toastObj => (
        <ToastMessageBlock id={toastObj.id} severity="info">
          {t('TrainingPlans.manageThreeDotMenu.activatePlanModalSuccessToast', {
            planName: itemByLanguage(plan?.name),
          })}
        </ToastMessageBlock>
      ));
      await options?.onSuccess?.(data, variables, onMutateResult, context);
    },
    onSettled: async (data, error, variables, onMutateResult, context) => {
      await invalidatePlanArchiveStatusQueries();
      await options?.onSettled?.(
        data,
        error,
        variables,
        onMutateResult,
        context,
      );
    },
    onError: async (error, variables, onMutateResult, context) => {
      datadogRum.addError(
        error instanceof Error ? error : new Error(JSON.stringify(error)),
      );
      toast.custom(toastObj => (
        <ToastMessageBlock id={toastObj.id} severity="error">
          {t('TrainingPlans.toastMessage.errorActivating')}
        </ToastMessageBlock>
      ));
      await options?.onError?.(error, variables, onMutateResult, context);
    },
  });
};
